// Helpers the admin screens add on top of M10's API client (src/lib/api.ts): reading a server
// refusal for display, file downloads (exports are files, not { data } envelopes) and the ISO 8601
// duration text settings use.
import { ApiError, currentPath, isApiError, type QueryValue, toQueryString } from '@/lib/api';

const DOWNLOAD_TIMEOUT_MS = 120_000;
const FRIENDLY_FALLBACK = 'Something went wrong. Try again in a moment.';
const NO_ACCESS = 'You don’t have access to this.';

/** The text to show for a failed call: the server's friendly detail, or a fallback. */
export function errorText(error: unknown): string {
  if (isApiError(error)) {
    return error.status === 403
      ? `${NO_ACCESS} ${error.detail === NO_ACCESS ? '' : error.detail}`.trim()
      : error.detail;
  }
  return FRIENDLY_FALLBACK;
}

/**
 * The server's field errors by path ("addresses.0" -> "That address is already registered…"),
 * for inline display next to the field. Paths are the request body's JSON paths.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const item of error.errors) {
    const key = item.path.replace(/^\/?body[./]?/, '').replaceAll('/', '.');
    if (!(key in result)) {
      result[key] = item.message;
    }
  }
  return result;
}

/** The first field error whose path starts with `prefix`, or undefined. */
export function fieldError(errors: Record<string, string>, prefix: string): string | undefined {
  const key = Object.keys(errors).find((k) => k === prefix || k.startsWith(`${prefix}.`));
  return key ? errors[key] : undefined;
}

function fileNameFrom(disposition: string | null, fallback: string): string {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/**
 * Downloads a file from the API (GET or POST with CSRF), saving it under the server's file name.
 * Throws ApiError on a refusal, so the page can show it.
 */
export async function downloadFile(
  path: string,
  options: { method?: 'GET' | 'POST'; query?: Record<string, QueryValue>; body?: unknown; csrfToken?: string },
  fallbackName: string,
): Promise<string> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (method !== 'GET' && options.csrfToken) {
    headers['x-csrf-token'] = options.csrfToken;
  }
  let response: Response;
  try {
    response = await fetch(`${path}${toQueryString(options.query)}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      signal: AbortSignal.timeout(DOWNLOAD_TIMEOUT_MS),
    });
  } catch {
    throw new ApiError(0, { code: 'network', detail: 'We couldn’t reach the Feedback tool. Try again.' });
  }
  if (response.status === 401) {
    window.location.assign(`/sign-in?returnTo=${encodeURIComponent(currentPath())}`);
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => ({}));
    throw new ApiError(response.status, problem as Record<string, unknown>);
  }
  const blob = await response.blob();
  const name = fileNameFrom(response.headers.get('content-disposition'), fallbackName);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a moment to start the download before the URL goes away.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}

// ---- ISO 8601 durations (setting values like PT15S, PT2M, P14D) ---------------------------------

const DURATION = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

function unit(n: number, one: string): string {
  return `${n} ${one}${n === 1 ? '' : 's'}`;
}

/** "PT15S" -> "15 seconds", "P1DT2H" -> "1 day 2 hours"; the input itself when it isn't a duration. */
export function describeDuration(value: string | null): string {
  if (!value) {
    return 'not set';
  }
  const match = DURATION.exec(value);
  if (!match) {
    return value;
  }
  const [, d, h, m, s] = match;
  const parts = [
    d ? unit(Number(d), 'day') : null,
    h ? unit(Number(h), 'hour') : null,
    m ? unit(Number(m), 'minute') : null,
    s ? unit(Number(s), 'second') : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : value;
}

/** "1 Oct 2026" style date for <input type="date"> values (YYYY-MM-DD, local). */
export function isoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
